/** biome-ignore-all lint/suspicious/noConsole: don't need logger here */
import * as Sentry from '@sentry/react'
import axios from 'axios'
import dayjs from 'dayjs'
import LogRocket from 'logrocket'
import { isDev, isProd } from 'v2/configs/constants'
import { embedSessionIds, type REmbedSession } from 'v2/shared/utils/embed-session'
import { isInternalSession } from 'v2/utils/internal-user'

enum LogLevel {
  LOG = 'LOG',
  ERROR = 'ERROR',
  WARN = 'WARN',
  INFO = 'INFO',
  DEBUG = 'DEBUG',
  CRITICAL = 'CRITICAL',
  REPORTED_WARN = 'REPORTED_WARN',
}

interface LoggerTags {
  orgName: string
  aomId: string
  roles: string
  email: string
  [key: string]: string
}

const tags: LoggerTags = {
  orgName: '',
  aomId: '',
  roles: '',
  email: '',
  name: '',
  region: '',
}

export const setLoggerTags = (loggerTags: Partial<LoggerTags>) => {
  try {
    Object.assign(tags, loggerTags)
    // Set tags in Sentry for better error tracking
    Sentry.setUser({ email: tags.email, id: tags.aomId })
    Object.entries(loggerTags).forEach(([key, value]) => {
      Sentry.setTag(key, value)
    })

    // Skipped for internal sessions: initLogRocket never ran for them, and the SDK
    // buffers pre-init calls until it hits 1000 and warns about a network fault.
    if (!isInternalSession()) {
      LogRocket.identify(tags.aomId, {
        email: tags.email,
        roles: tags.roles,
        orgName: tags.orgName,
      })
    }
  } catch (error) {
    console.error('Error setting logger tags', error)
  }
}

// Embed and hosted pages have no console user, so their events name the session instead. Merged
// into the logger's tags too: those override Sentry's on every event the logger captures.
export const setEmbedLoggerTags = (
  surface: 'embed' | 'hosted_kyc' | 'signatory',
  session: REmbedSession,
) => {
  const sessionTags = { surface, ...embedSessionIds(session) }
  Object.assign(tags, sessionTags)
  Sentry.setTags(sessionTags)
}

const getTags = () => tags

const formatLogMessage = (type: string, message: string) => {
  const timestamp = dayjs().format('D/MMM HH:mm:ss.SSS A')
  return `${timestamp} - CX: [${type}] ${message}`
}

// toJSON runs before a replacer, so `this[key]` is the raw value. An axios error's JSON carries the
// request's bearer and body; a plain Error's drops its message and stack.
function sentryArgReplacer(this: Record<string, unknown>, key: string, value: unknown) {
  const raw = this[key]
  // axios 0.21's isAxiosError throws on null.
  if (raw && axios.isAxiosError(raw)) {
    const { message, code, response, config } = raw
    return { message, code, status: response?.status, url: config?.url, method: config?.method }
  }
  return raw instanceof Error ? { ...raw, message: raw.message, stack: raw.stack } : value
}

// A failed stringify ships no args, never the raw ones.
export const toSentryArgs = (args: unknown[]): string[] => {
  try {
    return args.map((arg) => JSON.stringify(arg, sentryArgReplacer))
  } catch {
    return []
  }
}

const createLogMethod =
  (
    method: keyof Pick<Console, 'log' | 'error' | 'warn' | 'info' | 'debug'>,
    level: LogLevel,
    type: string,
    isLocalLog = false,
  ) =>
  (message: string, ...args: unknown[]) => {
    const formattedMessage = formatLogMessage(type, message)

    if (isProd) {
      // If in production, log to LogRocket except for local logs
      // Logrocket also captures local console logs by default
      if (isLocalLog) console.log(formattedMessage, ...args)
      else if (LogRocket?.[method] && !isInternalSession())
        LogRocket[method](formattedMessage, ...args)
    } else {
      console[method](formattedMessage, ...args)
    }

    // Sentry on both dev and prod
    if (isProd || isDev) {
      const sentryExtras = {
        tags: getTags(),
        extra: { args: toSentryArgs(args), formattedMessage },
      }

      const captureSentryEvent = (severityLevel: Sentry.SeverityLevel) => {
        // Find the first Error instance in args (can be at any position)
        const errorArg = args.find(
          (arg) => arg instanceof Error || (arg && typeof arg === 'object' && 'stack' in arg),
        )

        Sentry.withScope((scope) => {
          scope.setLevel(severityLevel)
          scope.setTags(sentryExtras.tags)
          scope.setExtras(sentryExtras.extra)

          if (errorArg) Sentry.captureException(errorArg)
          else Sentry.captureMessage(message, severityLevel)
        })
      }

      if (level === LogLevel.CRITICAL) captureSentryEvent('fatal')
      else if (level === LogLevel.ERROR) captureSentryEvent('error')
      else if (level === LogLevel.REPORTED_WARN) captureSentryEvent('warning')
    }
  }

interface Logger {
  log: (message: string, ...args: unknown[]) => void
  error: (message: string, ...args: unknown[]) => void
  warn: (message: string, ...args: unknown[]) => void
  info: (message: string, ...args: unknown[]) => void
  debug: (message: string, ...args: unknown[]) => void
  /**
   * For local logging in production
   */
  local: (message: string, ...args: unknown[]) => void
  /**
   * For critical errors that should be logged and reported to Sentry
   */
  critical: (message: string, ...args: unknown[]) => void
  /**
   * For expected failures still worth a Sentry event, at warning level (the error alerts skip it)
   */
  reportWarning: (message: string, ...args: unknown[]) => void
}

export const createLogger = (type: string): Logger => ({
  log: createLogMethod('log', LogLevel.LOG, type),
  error: createLogMethod('error', LogLevel.ERROR, type),
  warn: createLogMethod('warn', LogLevel.WARN, type),
  info: createLogMethod('info', LogLevel.INFO, type),
  debug: createLogMethod('debug', LogLevel.DEBUG, type),
  local: createLogMethod('log', LogLevel.LOG, type, true),
  critical: createLogMethod('error', LogLevel.CRITICAL, type),
  reportWarning: createLogMethod('warn', LogLevel.REPORTED_WARN, type),
})

// For backwards compatibility
export const logger = createLogger('App')
export const pubnubLogger = createLogger('PubNub')
export const phoneLogger = createLogger('Phone SDK')
export const tabLogger = createLogger('Tab Manager')
export const agentLogger = createLogger('Agent')
export const vibeAgentLogger = createLogger('VibeAgent')
